package top.wcpe.allincore.dsl.compiler.codegen.internal

import top.wcpe.allincore.dsl.compiler.ast.StmtAst
import top.wcpe.allincore.dsl.ir.codec.IrByteWriter

/**
 * 状态机序列化：负责把 [StateMachineLowerer.LowerResult] 写入字节流。
 *
 * 从 [ExprSerializer] 拆出，承接 STATE_MACHINE_HEADER / STATE_BLOCK / TRANSITION
 * 等 D2 wire format 写入逻辑。
 */
internal object StateMachineSerializer {

    // ===== D2-a 预留：状态机 wire format（IR v8 引入） =====
    //
    // 当前阶段仅定义常量，序列化器不会生成；reader 遇到这些 tag 直接抛
    // UnsupportedOperationException。正式落地见 D2-b（StateMachineExecutor）。
    //
    // 设计草案（D2-b 实施时再确认细节）：
    //   STMT_STATE_MACHINE_HEADER(16):
    //     u16 stateCount
    //     LP-UTF8 entryStateName
    //     u16 varCount, { LP-UTF8 name + u8 IrType ordinal }*
    //
    //   STMT_STATE_BLOCK(17):
    //     LP-UTF8 stateName
    //     u16 stmtCount, { stmt }*       —— 状态体内串行语句
    //     u16 transitionCount, { Transition }*
    //
    //   STMT_TRANSITION(18):
    //     u8 kind(0=AWAIT_SUCCESS,1=AWAIT_FAIL,2=AWAIT_TIMEOUT,3=AWAIT_PENDING,4=UNCONDITIONAL)
    //     LP-UTF8 nextStateName
    //     u8 hasBindVar(0/1) + LP-UTF8 bindVarName?
    //
    //   STMT_TERMINAL(19):
    //     u8 kind(0=RETURN,1=RETURN_VALUE,2=THROW)
    //     hasValue(u8) + (Expr value)?
    private const val STMT_STATE_MACHINE_HEADER = 16
    private const val STMT_STATE_BLOCK = 17
    private const val STMT_TRANSITION = 18
    @Suppress("unused")
    private const val STMT_TERMINAL = 19

    // ===== v9 新增：STMT_TRANSITION 头部 kind 字节常量（M4 A2 Stage 2） =====
    // 0 = NetRequest 旧形态（await net.request(...)）
    // 1 = SuspendCall 形态（await suspend lambda 调用，Stage 3 才会生成）
    private const val TRANSITION_KIND_NET_REQUEST = 0
    private const val TRANSITION_KIND_SUSPEND_CALL = 1

    /** suspend lambda 返回值在 state record 中的固定槽位名。 */
    internal const val SUSPEND_RETURN_VAR: String = "__return_value"

    /**
     * D2-b 写入状态机字节流：1 个 STATE_MACHINE_HEADER + N 个 STATE_BLOCK。
     *
     * 调用前 caller 必须已 writeShort(1) 把 stmtCount 写好（因为状态机本质是 1 条 stmt）。
     */
    internal fun writeStateMachine(out: IrByteWriter, lowered: StateMachineLowerer.LowerResult) {
        out.writeByte(STMT_STATE_MACHINE_HEADER)
        // header
        require(lowered.blocks.size <= SerializerLimits.U16_MAX) { "状态机 block 数超过 65535" }
        out.writeShort(lowered.blocks.size)
        out.writeShort(lowered.entryStateId)
        require(lowered.recordSchema.size <= SerializerLimits.U16_MAX) { "state record schema var 数超过 65535" }
        out.writeShort(lowered.recordSchema.size)
        for ((name, type) in lowered.recordSchema) {
            ExprSerializer.writeLpString(out, name)
            out.writeByte(StateMachineLowerer.typeOrdinal(type))
        }
        // blocks（按 id 升序）
        val sorted = lowered.blocks.sortedBy { it.blockId }
        for (b in sorted) writeStateBlock(out, b)
    }

    private fun writeStateBlock(out: IrByteWriter, block: StateMachineLowerer.BlockIr) {
        out.writeByte(STMT_STATE_BLOCK)
        out.writeShort(block.blockId)
        require(block.stmts.size <= SerializerLimits.U16_MAX) { "state block 内 stmt 数超过 65535" }
        out.writeShort(block.stmts.size)
        for (s in block.stmts) StmtSerializer.writeStmt(out, s)
        when (val t = block.terminator) {
            is StateMachineLowerer.Terminator.Transition -> {
                out.writeByte(0) // 0=transition
                writeTransition(out, t)
            }
            is StateMachineLowerer.Terminator.Natural -> {
                out.writeByte(1) // 1=terminal_natural
            }
            is StateMachineLowerer.Terminator.ReturnT -> {
                out.writeByte(2) // 2=terminal_return
                if (t.value == null) {
                    out.writeByte(0)
                } else {
                    out.writeByte(1)
                    ExprSerializer.writeExpr(out, t.value)
                }
            }
            is StateMachineLowerer.Terminator.Goto -> {
                // D2-c：无条件跳转。
                out.writeByte(3)
                out.writeShort(t.nextStateId)
            }
            is StateMachineLowerer.Terminator.Branch -> {
                // D2-c：条件跳转。控制流内含 await 时使用。
                out.writeByte(4)
                ExprSerializer.writeExpr(out, t.cond)
                out.writeShort(t.thenStateId)
                out.writeShort(t.elseStateId)
            }
            is StateMachineLowerer.Terminator.Delay -> {
                // delay(ms)：暂停后跳到 nextStateId。
                out.writeByte(5)
                ExprSerializer.writeExpr(out, t.delayMs)
                out.writeShort(t.nextStateId)
            }
        }
    }

    private fun writeTransition(out: IrByteWriter, t: StateMachineLowerer.Terminator.Transition) {
        out.writeByte(STMT_TRANSITION)
        if (t.suspendCallee != null) {
            // M4 A2 Stage 3：suspend lambda 形态。
            out.writeByte(TRANSITION_KIND_SUSPEND_CALL)
            ExprSerializer.writeExpr(out, t.suspendCallee)
            require(t.suspendArgs.size <= SerializerLimits.U16_MAX) { "transition suspendArgs 数超过 65535" }
            out.writeShort(t.suspendArgs.size)
            for (a in t.suspendArgs) ExprSerializer.writeExpr(out, a)
            out.writeShort(t.successNextStateId)
            out.writeShort(t.failureNextStateId)
            out.writeShort(t.timeoutNextStateId)
            if (t.bindVarName == null) {
                out.writeByte(0)
            } else {
                out.writeByte(1)
                ExprSerializer.writeLpString(out, t.bindVarName)
            }
            // suspend 形态强制 hasTailExpr = 0，与 reader 约束保持一致
            out.writeByte(0)
            return
        }
        // 旧 net.request 形态（kind=0）
        out.writeByte(TRANSITION_KIND_NET_REQUEST)
        val net = t.await
            ?: error("Transition.await 不应为 null（net.request 形态）")
        ExprSerializer.writeLpString(out, net.namespace)
        ExprSerializer.writeLpString(out, net.handler)
        require(net.payload.size <= SerializerLimits.U16_MAX) { "transition payload 项数超过 65535" }
        out.writeShort(net.payload.size)
        for (entry in net.payload) {
            ExprSerializer.writeLpString(out, entry.key)
            ExprSerializer.writeExpr(out, entry.value)
        }
        if (net.timeoutMs == null) {
            out.writeByte(0)
        } else {
            out.writeByte(1)
            ExprSerializer.writeExpr(out, net.timeoutMs)
        }
        out.writeShort(t.successNextStateId)
        out.writeShort(t.failureNextStateId)
        out.writeShort(t.timeoutNextStateId)
        if (t.bindVarName == null) {
            out.writeByte(0)
        } else {
            out.writeByte(1)
            ExprSerializer.writeLpString(out, t.bindVarName)
        }
        if (t.tailExpr == null) {
            out.writeByte(0)
        } else {
            out.writeByte(1)
            ExprSerializer.writeExpr(out, t.tailExpr)
        }
    }

    /**
     * suspend lambda 体改写：把末位 ExprStmt 转换为 `var __return_value = <expr>`，
     * 让 StateMachineLowerer 把该槽位写入 schema，runtime 终结后读出作为返回值。
     *
     * <p>末位非 ExprStmt 时不做转换（[top.wcpe.allincore.dsl.compiler.semantic.AwaitPositionChecker]
     * E608 已在编译期拦下；测试场景下手工构造的非标准 body 直接原样写出，runtime 读 __return_value
     * 不存在时返回 null）。空 body 同样原样保留。</p>
     */
    internal fun transformSuspendLambdaBody(body: List<StmtAst>): List<StmtAst> {
        if (body.isEmpty()) return body
        val tail = body.last() as? StmtAst.ExprStmt ?: return body
        val rewritten = StmtAst.VarDeclStmt(SUSPEND_RETURN_VAR, tail.call, tail.position)
        return body.dropLast(1) + rewritten
    }
}
